import NETWORKS_VK_ADS_V1 from 'api/networks/vk-ads/v1'
import { call, put, select, takeLatest } from 'redux-saga/effects'
import {
  FETCH,
  FETCH_ADGROUPS_COUNTER,
  FETCH_BANNERS_COUNTER,
  MERGE_STATISTICS,
  PROCESS_GROUP_ACTION_WITH_CONFIRMATION,
  REFRESH_STATISTICS,
  RENAME,
  SET_COUNTER,
  SET_DATA,
  SET_SELECTION,
  TOGGLE_GROUP_PROCESSING,
  TOGGLE_LOADING,
  UPDATE_ITEM_BY_ID,
  UPDATE_ITEMS,
  UPDATE_ITEMS_BY_ID,
} from 'modules/Services/CampaignsEditor/types'
import {
  selectCEFilters,
  selectCECurrentResource,
  selectCEResource,
  selectCESelectionIds,
} from 'modules/Services/CampaignsEditor/store/selectors'
import prepareFilterParams from 'utils/prepareFilterParams'
import omit from 'lodash/omit'
import { confirmSaga } from 'store/saga-utils/confirm.saga'
import { cancellableCall } from 'store/saga-utils/cancellableCall.saga'
import extractErrors from 'utils/extractErrors'
import i18n from 'i18next'
import { notificationShow } from 'modules/Notifications/actions'

function* getFilters() {
  return prepareFilterParams(yield select(selectCEFilters))
}

function* getCurrentResource() {
  return yield select(selectCECurrentResource)
}

function* loadStatisticsByExternalIds({ external_ids }) {
  const currentResource = yield call(getCurrentResource)

  let METHOD

  switch (currentResource) {
    case 'campaigns':
      METHOD = NETWORKS_VK_ADS_V1.fetchCampaignsStatistics
      break
    case 'ad-groups':
      METHOD = NETWORKS_VK_ADS_V1.fetchAdGroupsStatistics
      break
    case 'banners':
      METHOD = NETWORKS_VK_ADS_V1.fetchBannersStatistics
      break
  }

  try {
    const params = yield call(getFilters)

    const { response, error } = yield call(METHOD, {
      params: { ...params, external_ids },
    })

    if (error) {
      throw error
    }

    return { items: response.data.items }
  } catch (error) {
    console.logXHR(error)
    return { error }
  }
}

function* getCurrentResourceState() {
  const currentResource = yield call(getCurrentResource)
  return yield select(selectCEResource(currentResource))
}

function* loadList({ payload: { params = {} } = {} }) {
  const currentResource = yield call(getCurrentResource)

  if (currentResource === null) {
    return
  }

  const { sorting, pagination } = yield call(getCurrentResourceState)

  const { campaignsSelectionIds, adGroupsSelectionIds } = yield select(
    (state) => ({
      campaignsSelectionIds: selectCESelectionIds('campaigns')(state),
      adGroupsSelectionIds: selectCESelectionIds('ad-groups')(state),
    }),
  )

  let METHOD,
    selectionParams = {}

  switch (currentResource) {
    case 'campaigns':
      METHOD = NETWORKS_VK_ADS_V1.fetchCampaigns
      break
    case 'ad-groups':
      METHOD = NETWORKS_VK_ADS_V1.fetchAdGroups
      selectionParams = {
        campaign_external_ids: Object.keys(campaignsSelectionIds).map(
          (id) => +id,
        ),
      }
      break
    case 'banners':
      METHOD = NETWORKS_VK_ADS_V1.fetchBanners
      selectionParams = {
        campaign_external_ids: Object.keys(campaignsSelectionIds).map(
          (id) => +id,
        ),
        ad_group_external_ids: Object.keys(adGroupsSelectionIds).map(
          (id) => +id,
        ),
      }
      break
  }

  try {
    const { id, desc } = sorting?.[0] ?? {}
    const sortingParams = id
      ? {
          sort: id,
          direction: desc ? 'desc' : 'asc',
        }
      : {}

    const filterParams = yield call(getFilters)

    yield put({
      type: TOGGLE_LOADING,
      payload: true,
    })

    const { response, error } = yield call(METHOD, {
      params: {
        offset: pagination.limit * (pagination.page - 1),
        limit: pagination.limit,
        ...filterParams,
        ...selectionParams,
        ...sortingParams,
        ...params,
      },
    })

    if (error) {
      throw error
    }

    const responseItems = response.data.items

    const { items: statisticsRows } = yield call(loadStatisticsByExternalIds, {
      external_ids: responseItems.map((i) => i.external_id),
    })

    let nodeName

    switch (currentResource) {
      case 'campaigns':
        nodeName = 'campaign'
        break
      case 'ad-groups':
        nodeName = 'ad_group'
        break
      case 'banners':
        nodeName = 'banner'
        break
    }

    statisticsRows.map((item) => {
      const index = responseItems.findIndex(
        (i) => i.external_id === item[nodeName].external_id,
      )

      if (index > -1) {
        responseItems[index] = {
          ...responseItems[index],
          ...omit(item, nodeName),
        }
      }
    })

    yield put({
      type: SET_DATA,
      payload: { ...response.data, items: responseItems },
    })
  } catch (error) {
    console.logXHR(error)
  } finally {
    yield put({
      type: TOGGLE_LOADING,
      payload: false,
    })
  }
}

function* refreshStatistics() {
  const { items: currentItems } = yield call(getCurrentResourceState)

  const { items: statisticsRows } = yield call(loadStatisticsByExternalIds, {
    external_ids: currentItems.map((i) => i.external_id),
  })

  if (statisticsRows) {
    yield put({
      type: MERGE_STATISTICS,
      payload: { items: statisticsRows },
    })
  }
}

function* rename({ payload: { id, name } }) {
  const currentResource = yield call(getCurrentResource)

  let METHOD

  switch (currentResource) {
    case 'campaigns':
      METHOD = NETWORKS_VK_ADS_V1.renameCampaign
      break
    case 'ad-groups':
      METHOD = NETWORKS_VK_ADS_V1.renameAdGroup
      break
    case 'banners':
      METHOD = NETWORKS_VK_ADS_V1.renameBanner
      break
  }

  try {
    const { error } = yield call(METHOD, {
      external_id: id,
      data: { name },
    })

    if (error) {
      throw error
    }

    yield put({ type: UPDATE_ITEM_BY_ID, payload: { external_id: id, name } })

    yield put(
      notificationShow({
        type: 'success',
        message: 'common::notifications.data_success_stored',
      }),
    )
  } catch (error) {
    console.logXHR(error)
  }
}

function* fetchAdGroupsCounter() {
  const filterParams = yield call(getFilters)

  const campaignSelection = yield select(selectCESelectionIds('campaigns'))

  try {
    const { response, error } = yield call(
      NETWORKS_VK_ADS_V1.fetchAdGroupsCounter,
      {
        params: {
          offset: 0,
          limit: 100000,
          ...filterParams,
          campaign_external_ids: Object.keys(campaignSelection).map(
            (id) => +id,
          ),
        },
      },
    )

    if (error) {
      throw error
    }

    yield put({
      type: SET_COUNTER,
      payload: { ['ad-groups']: response.data || 0 },
    })
  } catch (error) {
    console.logXHR(error)
  }
}

function* fetchBannersCounter() {
  const filterParams = yield call(getFilters)

  const { campaignSelection, adGroupsSelection } = yield select((state) => ({
    campaignSelection: selectCESelectionIds('campaigns')(state),
    adGroupsSelection: selectCESelectionIds('ad-groups')(state),
  }))

  try {
    const { response, error } = yield call(
      NETWORKS_VK_ADS_V1.fetchBannersCounter,
      {
        params: {
          offset: 0,
          limit: 100000,
          ...filterParams,
          campaign_external_ids: Object.keys(campaignSelection).map(
            (id) => +id,
          ),
          ad_group_external_ids: Object.keys(adGroupsSelection).map(
            (id) => +id,
          ),
        },
      },
    )

    if (error) {
      throw error
    }

    yield put({
      type: SET_COUNTER,
      payload: { banners: response.data || 0 },
    })
  } catch (error) {
    console.logXHR(error)
  }
}

function* processGroupActionWithConfirmation({
  payload,
  meta: { resolve = () => {}, reject = () => {} } = {},
}) {
  try {
    const [confirmed, UUID] = yield call(confirmSaga, {
      modalType: 'confirmation',
      ...payload,
    })

    yield put({ type: 'MODAL/HIDE', payload: { UUID } })

    if (confirmed) {
      const { response, error } = yield call(processSelection, {
        payload,
      })

      if (error) {
        throw error
      }

      resolve(response)
    }
  } catch (error) {
    reject(error)
    console.logXHR(error)
  }
}

function* processResultsNotification({ processResults, action, resource }) {
  if (processResults.success.length > 0) {
    yield put(
      notificationShow({
        type: 'success',
        message: i18n.t(
          `campaigns_editor::actions.group_notifications.${action}.success`,
          {
            context: resource,
            count: processResults.success.length,
          },
        ),
      }),
    )
  }

  if (processResults.failed.length > 0) {
    yield put(
      notificationShow({
        type: 'error',
        message: i18n.t(
          `campaigns_editor::actions.group_notifications.${action}.failed`,
          {
            context: resource,
            count: processResults.failed.length,
          },
        ),
      }),
    )
  }
}

function* processSelection({ payload }) {
  const { items: currentItems } = yield call(getCurrentResourceState)

  try {
    yield put({
      type: TOGGLE_GROUP_PROCESSING,
    })

    const { response, error, canceled } = yield cancellableCall(
      payload.API_CALL,
      {
        resource: payload.resource,
        data: { external_ids: payload.ids },
      },
      arguments,
    )

    if (canceled) return

    if (error) {
      throw error
    }

    const processResults = response.data.reduce(
      (acc, { external_id: resultId, status }) => {
        const rowData = currentItems.find(
          ({ external_id }) => external_id === resultId,
        )

        if (status === 'success') {
          acc.success = [...acc.success, { ...rowData }]
        } else {
          acc.failed = [...acc.failed, { ...rowData }]
        }
        return acc
      },
      { success: [], failed: [] },
    )

    yield call(processResultsNotification, {
      processResults,
      action: payload.groupActionType,
      resource: payload.resource,
    })

    yield put({ type: SET_SELECTION, payload: { ids: [] } })

    return { response, undefined }
  } catch (error) {
    const { form } = extractErrors(error.errors)
    yield put(
      notificationShow({
        type: 'error',
        message: form || 'common::notifications.data_update_failed',
      }),
    )
    return { undefined, error }
  } finally {
    yield put({
      type: TOGGLE_GROUP_PROCESSING,
    })
  }
}

function* updateItemsBy({
  payload: { data, removeRowCondition = () => false, updater = () => {} },
}) {
  const { items: currentItems } = yield call(getCurrentResourceState)

  const filters = yield select(selectCEFilters)

  if (removeRowCondition(filters)) {
    const processRows =
      data
        .filter(({ status }) => status === 'success')
        .map(({ externalId }) => externalId) ?? []

    if (processRows.length > 0) {
      yield call(remove, processRows)

      yield put({
        type: FETCH,
        payload: {
          params: {
            offset: currentItems.length - processRows.length,
            limit: processRows.length,
          },
        },
      })
    }
  } else {
    const updatedRows = currentItems.map((row) => {
      const inResponse = data.find(
        ({ externalId }) => externalId === row.external_id,
      )
      return inResponse ? updater(row, inResponse) : row
    })

    yield put({
      type: UPDATE_ITEMS,
      payload: { items: updatedRows },
    })
  }
}

function* remove(processRows) {
  const { items: currentItems } = yield call(getCurrentResourceState)

  const updatedRows = currentItems.filter(
    ({ external_id }) => !processRows.includes(external_id),
  )

  yield put({
    type: UPDATE_ITEMS,
    payload: { items: updatedRows },
  })
}

export default function* Sagas() {
  yield takeLatest(FETCH, loadList)
  yield takeLatest(REFRESH_STATISTICS, refreshStatistics)
  yield takeLatest(RENAME, rename)
  yield takeLatest(FETCH_ADGROUPS_COUNTER, fetchAdGroupsCounter)
  yield takeLatest(FETCH_BANNERS_COUNTER, fetchBannersCounter)
  yield takeLatest(
    PROCESS_GROUP_ACTION_WITH_CONFIRMATION,
    processGroupActionWithConfirmation,
  )
  yield takeLatest(UPDATE_ITEMS_BY_ID, updateItemsBy)
}

export { getCurrentResourceState }
